import type { ISbStoryData } from '@storyblok/js';
import type { MaybeRefOrGetter } from 'vue';
import type { StoryblokMultilink } from '~/.storyblok/types/storyblok';
import { normalizeInternalLink } from '~/utils/normalizeInternalLink';
import { isResolvableStoryblokLink, resolveStoryblokLink, buildUrlFromStory } from '~/utils/resolveStoryblokLink';

const normalizePath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

const toAbsoluteUrl = (origin: string, path: string) => {
    const absoluteUrl =
        path.startsWith('http://') || path.startsWith('https://') ? path : `${origin}${normalizePath(path)}`;
    return normalizeInternalLink(absoluteUrl, origin);
};

const getLangFromRequestPath = (pathname: string, countryLanguages: ReturnType<typeof useCountryLanguages>) => {
    const pageLocale = parseLocaleFromPath(pathname);
    if (!pageLocale?.country) {
        return null;
    }
    return pageLocale.language ?? countryLanguages[pageLocale.country]?.defaultLanguage ?? null;
};

export const useCanonicalUrl = (
    canonicalLink?: MaybeRefOrGetter<ISbStoryData<unknown> | StoryblokMultilink | undefined>
) => {
    const requestURL = useRequestURL();
    // Behind the ALB the request host is the internal one, so absolute URLs
    // must be derived from the configured public origin instead.
    const baseUrl = useRuntimeConfig().public.baseUrl;
    const countryLanguages = useCountryLanguages();

    return computed(() => {
        const thisPageUrl = toAbsoluteUrl(baseUrl, requestURL.pathname);

        if (!canonicalLink) {
            return thisPageUrl;
        }

        const canonicalSource = toValue(canonicalLink);
        const lang = getLangFromRequestPath(requestURL.pathname, countryLanguages);

        if (!lang) {
            return thisPageUrl;
        }

        try {
            if (isResolvableStoryblokLink(canonicalSource)) {
                return toAbsoluteUrl(baseUrl, resolveStoryblokLink(canonicalSource, countryLanguages, lang));
            }

            if (canonicalSource && 'default_full_slug' in canonicalSource) {
                return toAbsoluteUrl(baseUrl, buildUrlFromStory(canonicalSource, countryLanguages, lang));
            }
        } catch (error) {
            log.error(`[useCanonicalUrl] Error resolving storyblok link, using fallback URL: ${thisPageUrl}`, error);
        }

        return thisPageUrl;
    });
};
